import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass, Package, User, ShieldCheck, SignOut, PencilSimple } from "@phosphor-icons/react";
import { fetchProductClasses } from "@/lib/api";
import ProductClassEditModal from "@/components/ProductClassEditModal";
import SortableTh, { nextSort, compareBy } from "@/components/SortableTh";
import { HelpPill } from "@/components/HeaderBrand";
import { useT } from "@/lib/i18n";

export default function ProductClassesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const tx = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [sortKey, setSortKey] = useState("product_class");
  const [sortDir, setSortDir] = useState("asc");
  const handleSort = (col) => {
    const { key, dir } = nextSort(sortKey, sortDir, col);
    setSortKey(key); setSortDir(dir);
  };

  const loadItems = () => {
    if (!user?.is_admin) return;
    setLoading(true);
    fetchProductClasses(user.email)
      .then((d) => setItems(d.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user?.is_admin) {
      setErr("Admins only. Please log in with an admin account.");
      setLoading(false);
      return;
    }
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const productTypes = useMemo(() => {
    const set = new Set();
    for (const i of items) if (i.product_type) set.add(i.product_type);
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (typeFilter !== "all" && i.product_type !== typeFilter) return false;
      if (!q) return true;
      const hay = `${i.product_class || ""} ${i.product_type || ""} ${i.computed_key || ""} ${i.spec_1 || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, typeFilter]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const i of filtered) {
      const k = i.product_type || "(Uncategorized)";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(i);
    }
    const mult = sortDir === "desc" ? -1 : 1;
    const sorter = (a, b) => mult * compareBy(a, b, sortKey || "product_class");
    for (const arr of map.values()) arr.sort(sorter);
    return Array.from(map.entries());
  }, [filtered, sortKey, sortDir]);

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <ShieldCheck size={48} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold mb-2">{tx("pc.admins_only", "Admins only")}</p>
          <button onClick={() => navigate("/")} className="text-blue-700 text-sm font-medium hover:underline">{tx("pc.back_home", "Back to home")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="product-classes-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="text-slate-500 hover:text-blue-700 flex items-center gap-1 text-sm font-medium" data-testid="pc-back">
          <ArrowLeft size={16} /> Home
        </Link>
        <div className="flex items-center gap-1.5">
          <Package size={18} weight="bold" className="text-blue-700" />
          <h1 className="text-lg md:text-xl font-bold font-['Chivo'] text-slate-900">{tx("pc.title", "Product Classes")}</h1>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
            <User size={14} /> {user?.email}
            <span className="text-[9px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 px-1 py-0.5 rounded-sm">Admin</span>
          </div>
          <HelpPill />
          <button onClick={onLogout} data-testid="pc-logout"
            className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-600" title={tx("compressor_page.sign_out", "Sign out")}>
            <SignOut size={15} />
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="pc-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={tx("pc.search_placeholder", "Search class, type, spec...")}
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <select data-testid="pc-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="all">{tx("pc.all_types", "All product types")}</option>
            {productTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase ml-auto">
            {filtered.length} / {items.length}
          </span>
        </div>

        {err && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-sm border border-red-200 mb-4" data-testid="pc-error">
            {err}
          </div>
        )}
        {loading && <div className="text-sm text-slate-500">Loading…</div>}

        {!loading && !err && groups.map(([type, rows]) => (
          <section key={type} className="mb-6" data-testid={`pc-group-${type}`}>
            <h2 className="text-sm font-bold font-['Chivo'] tracking-tight text-slate-800 bg-slate-100 px-3 py-1.5 rounded-sm inline-block mb-2">
              {type}
              <span className="ml-2 text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded-sm">{rows.length}</span>
            </h2>
            <div className="border border-slate-200 rounded-sm bg-white">
              <table className="w-full text-sm" data-testid={`pc-table-${type}`}>
                <thead className="sticky top-[52px] z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_0_rgb(226_232_240)]">
                  <tr className="text-left text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    <th className="px-2 py-2 w-10 bg-slate-50"></th>
                    <SortableTh column="product_class" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 bg-slate-50">{tx("pc.col_class_name", "Class Name")}</SortableTh>
                    <SortableTh column="spec_1" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 hidden md:table-cell bg-slate-50">{tx("pc.col_spec1", "Spec 1")}</SortableTh>
                    <SortableTh column="spec_2" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 hidden lg:table-cell bg-slate-50">{tx("pc.col_spec2", "Spec 2")}</SortableTh>
                    <SortableTh column="spec_3" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 hidden md:table-cell bg-slate-50">{tx("pc.col_spec3", "Spec 3")}</SortableTh>
                    <SortableTh column="needs_serial" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="center" className="px-3 py-2 text-center hidden sm:table-cell bg-slate-50">{tx("table.serial_num", "Serial #")}</SortableTh>
                    <SortableTh column="in_price_list" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="center" className="px-3 py-2 text-center bg-slate-50">{tx("pc.col_in_price_list", "In Price List")}</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.id} data-testid={`pc-row-${r.id}`}
                      className={`border-b border-slate-100 ${idx % 2 ? "bg-slate-50/50" : ""} hover:bg-blue-50/30`}>
                      <td className="px-2 py-2 text-center">
                        <button data-testid={`pc-edit-button-${r.id}`} onClick={() => setEditRow(r)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-sm border border-slate-200 hover:bg-blue-50 hover:border-blue-300 text-slate-500 hover:text-blue-700 transition-colors" title={tx("action.edit", "Edit")}>
                          <PencilSimple size={13} />
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900">{r.product_class || "—"}</div>
                        {r.computed_key && r.computed_key !== r.product_class && (
                          <div className="font-mono text-[10px] text-slate-400 mt-0.5">{r.computed_key}</div>
                        )}
                        {/* Mobile: stack specs under the name */}
                        <div className="md:hidden mt-1 space-y-0.5 text-[11px] text-slate-600">
                          {r.spec_1 && <div><span className="font-semibold">{r.spec_1}:</span> {r.spec_1_options || "—"}</div>}
                          {r.spec_3 && <div><span className="font-semibold">{r.spec_3}:</span> {r.spec_3_options || "—"}</div>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700 hidden md:table-cell">
                        {r.spec_1 || "—"}
                        {r.spec_1_options && <div className="text-[10px] text-slate-500 mt-0.5 max-w-[280px] truncate" title={r.spec_1_options}>{r.spec_1_options}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-700 hidden lg:table-cell">{r.spec_2 || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 hidden md:table-cell">
                        {r.spec_3 || "—"}
                        {r.spec_3_options && <div className="text-[10px] text-slate-500 mt-0.5 max-w-[260px] truncate" title={r.spec_3_options}>{r.spec_3_options}</div>}
                      </td>
                      <td className="px-3 py-2 text-center hidden sm:table-cell">
                        {r.needs_serial === "Yes" ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm">{tx("action.yes", "Yes")}</span>
                        ) : <span className="text-slate-400 text-xs">No</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {String(r.in_price_list) === "1" ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">On</span>
                        ) : <span className="text-slate-400 text-xs">Off</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </main>

      {editRow && (
        <ProductClassEditModal row={editRow} user={user}
          onClose={() => setEditRow(null)}
          onSaved={() => loadItems()} />
      )}
    </div>
  );
}
